import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from 'components/link';
import positions, { createTransformSourcePhoton } from 'config/images';
import withStyles from 'isomorphic-style-loader/withStyles';

import styles from './thumbnail.css';

export class Thumbnail extends Component {
  constructor(props) {
    super(props);

    this.positionConfig = positions[this.props.position];
    this.shouldUseSrcSet = 1 < this.positionConfig.sources.length;
    this.state = {
      currentSrc: this.getSrc(this.props.originalSource),
      currentSrcSet: this.getSrcSet(this.props.originalSource),
      currentSizes: this.getSizes(),
      isError: false,
      isLoading: true,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.originalSource !== nextProps.originalSource) {
      this.setState({
        currentSrc: this.getSrc(nextProps.originalSource),
        currentSrcSet: this.getSrcSet(nextProps.originalSource),
      });
    }
  }

  onErrorImage = () => this.setState({ isError: true });

  onLoadImage = () => this.setState({ isLoading: false });

  getSrc(originalSource) {
    const sourceParams = this.positionConfig.sources;

    // If zero sources are configured, return original image
    if (!sourceParams.length) {
      return originalSource;
    }

    // Use the configured default src, otherwise use the first configured src
    let defaultParams = sourceParams.filter(({ isDefault }) => isDefault);
    defaultParams = defaultParams.length ? defaultParams[0] : sourceParams[0];
    return this.transformSource(originalSource, defaultParams);
  }

  getSrcSet(originalSource) {
    // Only use srcset if there is more than one configured source
    if (!this.shouldUseSrcSet) {
      return null;
    }

    const sourceParams = this.positionConfig.sources;

    return sourceParams.map((params) => {
      const transformedSource = this.transformSource(
        originalSource,
        params,
      );
      return `${transformedSource} ${params.width}w`;
    }).join(',');
  }

  getSizes() {
    // Only use sizes attribute if there is more than one configured source
    if (!this.shouldUseSrcSet) {
      return null;
    }

    const sizes = this.positionConfig.sizes;

    // Return default value if no sizes specified
    if (!sizes.length) {
      return '100vw';
    }

    return sizes
      .map(({ mediaQuery, value }) => `${mediaQuery || ''} ${value}`)
      .join(',');
  }

  transformSource = (originalSource, params) => {
    const { transformFactory, position } = this.props;
    const transform = transformFactory(positions[position]);

    return transform(originalSource, params);
  };

  render() {
    const {
      alt,
      isLazyLoading,
      hideLinkFromAccessibility,
      wrapperClasses,
      children,
      imageLink,
      onClickImage,
      height,
      width,
    } = this.props;
    const {
      isLoading,
      isError,
      currentSrc,
      currentSrcSet,
      currentSizes,
    } = this.state;

    const imgComponent = isLazyLoading ?
      (<img
        className="lazyload"
        data-srcset={currentSrcSet}
        data-sizes={currentSizes}
        data-src={currentSrc}
        alt={alt}
        onLoad={this.onLoadImage}
        onError={this.onErrorImage}
        height={height} // 16:9 aspect ratio
        width={width}
        key={currentSrc}
      />)
      :
      (<img
        srcSet={currentSrcSet}
        sizes={currentSizes}
        src={currentSrc}
        alt={alt}
        onLoad={this.onLoadImage}
        onError={this.onErrorImage}
        height={height} // 16:9 aspect ratio
        width={width}
      />);

    const imgWrapperClasses = classNames(
      'imageWrapper',
      styles.imageWrapper,
      { [styles.loading]: isLoading && !isError },
    );

    return (
      <figure
        className={classNames(
          'featuredImage',
          wrapperClasses,
        )}
      >
        {imageLink ?
          <Link
            className={imgWrapperClasses}
            to={imageLink}
            onClick={onClickImage}
            {...hideLinkFromAccessibility ? { 'aria-hidden': true, tabIndex: -1 } : {}}
          >
            {imgComponent}
          </Link>
          :
          <div className={imgWrapperClasses}>
            {imgComponent}
          </div>
        }
        {children &&
          <figcaption className="imageCaption">
            {children}
          </figcaption>
        }
      </figure>
    );
  }
}

Thumbnail.propTypes = {
  alt: PropTypes.string,
  children: PropTypes.node,
  isLazyLoading: PropTypes.bool,
  originalSource: PropTypes.string.isRequired,
  position: PropTypes.oneOf(Object.keys(positions)),
  transformFactory: PropTypes.func,
  wrapperClasses: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  imageLink: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  height: PropTypes.string,
  width: PropTypes.string,
  onClickImage: PropTypes.func,
  hideLinkFromAccessibility: PropTypes.bool,
};

Thumbnail.defaultProps = {
  alt: '',
  isLazyLoading: false,
  position: 'full',
  children: null,
  transformFactory: createTransformSourcePhoton,
  wrapperClasses: '',
  imageLink: '',
  height: '360', // 16:9 aspect ratio
  width: '640',
  onClickImage: () => {},
  hideLinkFromAccessibility: false,
};

export default withStyles(styles)(Thumbnail);
